/**
 * @file dev-mode.ts
 * @description 개발자 모드 체크 유틸리티 (클라이언트/서버 공통)
 *
 * 개발자 모드는 두 가지 방식으로 작동합니다:
 * 1. 개발 서버: 자동 활성화 (NODE_ENV === 'development')
 * 2. 프로덕션: 개발자 계정 + localStorage 스위치 활성화
 *
 * 주요 기능:
 * 1. 개발 서버에서 자동 활성화
 * 2. 프로덕션에서 개발자 계정만 스위치 사용 가능
 * 3. localStorage 기반 상태 관리
 *
 * 사용 방법:
 * - 클라이언트: `isDevMode()` 또는 `useDevMode()` 훅 사용
 * - 서버: `isDevMode()` 함수 직접 사용 (서버에서는 개발 서버만 체크)
 */

const DEV_MODE_STORAGE_KEY = "bidix-dev-mode-enabled";

/**
 * 개발자 모드 활성화 여부 확인
 *
 * - 개발 서버: localStorage 스위치 확인 (기본값: true, 스위치로 온오프 가능)
 * - 프로덕션: 개발자 계정 + localStorage 스위치 확인
 *
 * @param isDeveloperAccount 개발자 계정 여부 (클라이언트에서만 사용)
 * @returns 개발자 모드 활성화 여부
 */
export function isDevMode(isDeveloperAccount?: boolean): boolean {
  // 개발 서버: localStorage 스위치 확인 (스위치로 온오프 가능)
  if (process.env.NODE_ENV === "development") {
    // 서버 사이드에서는 개발 서버일 때 true 반환
    if (typeof window === "undefined") {
      return true;
    }

    // 클라이언트 사이드: localStorage 스위치 확인
    try {
      const stored = localStorage.getItem(DEV_MODE_STORAGE_KEY);
      // localStorage에 값이 없으면 기본값 true (개발 서버이므로)
      // 값이 있으면 그 값 사용 (스위치로 false로 설정 가능)
      const enabled = stored === null ? true : stored === "true";

      console.log("[dev-mode] 개발 서버 - 개발자 모드 상태:", {
        isDevMode: enabled,
        stored,
        NODE_ENV: process.env.NODE_ENV,
        timestamp: new Date().toISOString(),
      });

      return enabled;
    } catch (error) {
      console.error("[dev-mode] localStorage 접근 오류:", error);
      // 에러 시 기본값 true 반환
      return true;
    }
  }

  // 프로덕션 환경
  // 서버 사이드에서는 개발자 계정 정보를 알 수 없으므로 false
  if (typeof window === "undefined") {
    return false;
  }

  // 클라이언트 사이드: 개발자 계정이 아니면 항상 false
  if (isDeveloperAccount === false) {
    return false;
  }

  // 개발자 계정이고 localStorage에서 스위치 상태 확인
  try {
    const stored = localStorage.getItem(DEV_MODE_STORAGE_KEY);
    const enabled = stored === "true";

    console.log("[dev-mode] 프로덕션 - 개발자 모드 상태:", {
      isDevMode: enabled,
      isDeveloperAccount,
      stored,
      NODE_ENV: process.env.NODE_ENV,
      timestamp: new Date().toISOString(),
    });

    return enabled;
  } catch (error) {
    console.error("[dev-mode] localStorage 접근 오류:", error);
    return false;
  }
}

/**
 * 개발자 모드 스위치 상태 설정 (클라이언트 전용)
 *
 * @param enabled 활성화 여부
 */
export function setDevMode(enabled: boolean): void {
  if (typeof window === "undefined") {
    console.warn(
      "[dev-mode] 서버 사이드에서는 setDevMode를 호출할 수 없습니다.",
    );
    return;
  }

  try {
    if (enabled) {
      localStorage.setItem(DEV_MODE_STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(DEV_MODE_STORAGE_KEY);
    }

    console.log("[dev-mode] 개발자 모드 스위치 상태 변경:", {
      enabled,
      timestamp: new Date().toISOString(),
    });

    // 상태 변경을 다른 컴포넌트에 알리기 위해 이벤트 발생
    window.dispatchEvent(
      new CustomEvent("dev-mode-changed", { detail: { enabled } }),
    );
  } catch (error) {
    console.error("[dev-mode] localStorage 설정 오류:", error);
  }
}

/**
 * 개발자 모드 스위치 상태 가져오기 (클라이언트 전용)
 *
 * @returns 현재 스위치 상태
 */
export function getDevModeState(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const stored = localStorage.getItem(DEV_MODE_STORAGE_KEY);
    return stored === "true";
  } catch (error) {
    console.error("[dev-mode] localStorage 읽기 오류:", error);
    return false;
  }
}

/**
 * 개발자 모드가 활성화되어 있으면 모든 기능을 사용할 수 있는지 확인
 *
 * @param isDeveloperAccount 개발자 계정 여부 (클라이언트에서만 사용)
 * @returns 모든 기능 활성화 여부
 */
export function isAllFeaturesEnabled(isDeveloperAccount?: boolean): boolean {
  return isDevMode(isDeveloperAccount);
}
